import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { BsArrowDownShort, BsArrowUpShort } from 'react-icons/bs';
import { IoClose, IoWarning } from 'react-icons/io5';
import styled from 'styled-components';
import { formatDateWithTimezoneOffset } from '../../../../helpers/utils';
import LoadingAnimation from '../../../loadingAnimation';
import type { RootState } from '../../../../state/store';
import type { ColumnDef, TableSearchListConfig } from './types';

// Extend TanStack Table types to include our custom meta properties
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    type?: string;
    enable_sorting?: boolean;
    header_prefix?: string;
    header_suffix?: string;
    default_sort_order?: 'asc' | 'desc';
    aggregate?: boolean;
    value_formatter?: string;
  }
}

interface TableVisualProps {
  data: Array<Record<string, any>>;
  config?: TableSearchListConfig['config']['table'];
  moreDetailsConfig?: TableSearchListConfig['config']['more_details_dialog'];
  width: number;
  height: number;
  onOrderClick: (orderId: string) => Promise<any>; // Function to fetch order details
}

const TableVisual: React.FC<TableVisualProps> = ({
  data,
  config,
  moreDetailsConfig,
  width = 800,
  height = 400,
  onOrderClick
}: TableVisualProps) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const columnHelper = createColumnHelper();

  // Initialize sorting with default values from config
  const [sorting, setSorting] = useState<SortingState>(() => {
    // Look for columns with default_sort_order
    if (config?.columnDefs) {
      const defaultSortColumn = config.columnDefs.find(
        (col) =>
          col.default_sort_order &&
          col.enable_sorting === true &&
          (col.type === 'Date' || col.type === 'DateOnly' || col.type === 'Number')
      );

      if (defaultSortColumn) {
        return [
          {
            id: defaultSortColumn.accessor,
            desc: defaultSortColumn.default_sort_order === 'desc'
          }
        ];
      }
    }

    // Fallback to config-level default sort
    if (config?.default_sort_accessor && config?.default_sort_order) {
      return [
        {
          id: config.default_sort_accessor,
          desc: config.default_sort_order === 'desc'
        }
      ];
    }
    return [];
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Record<string, any> | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState<any>(null);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const getAggregatedData = (rowData: Array<Record<string, any>>, columnData: Array<ColumnDef>) => {
    let emptyColumns = 0;
    const summaryData = columnData.map((c, i) => {
      if (i === 0) {
        return 'Total';
      }
      if (c.type === 'Number') {
        const total = rowData.reduce((total, d) => {
          return total + parseFloat(d[c.accessor] || 0);
        }, 0);
        return formatNumber(total);
      } else {
        emptyColumns++;
        return '';
      }
    });
    return emptyColumns === summaryData.length - 1 ? [] : summaryData;
  };

  const formatColumnHeader = (columnDef: ColumnDef) => {
    let header = columnDef.header;

    if (columnDef.header_prefix) {
      header = `${columnDef.header_prefix} ${header}`;
    }

    if (columnDef.header_suffix) {
      header = `${header} ${columnDef.header_suffix}`;
    }

    return header;
  };

  const formatCell = ({ cell }: { cell: any }) => {
    let cellContent = cell.renderValue();
    const columnType = cell.column.columnDef.meta?.type ?? 'Text';
    const timezoneOffset = cell.row.original?.offset;

    if (['Date', 'DateOnly'].includes(columnType)) {
      const timestampValue = cell.renderValue();
      cellContent =
        timestampValue != 0 && timezoneOffset
          ? formatDateWithTimezoneOffset(
              timestampValue.toString(),
              timezoneOffset,
              columnType === 'Date' ? 'yyyy-MM-dd hh:mm:ss aa' : 'MMM dd, yyyy'
            )
          : '-';
    } else if (columnType === 'Number') {
      cellContent = formatNumber(parseFloat(cellContent || 0));
    }

    return cellContent;
  };

  const handlePrimaryCellClick = async (rowData: Record<string, any>) => {
    if (moreDetailsConfig?.enabled && onOrderClick) {
      setSelectedRowData(rowData);
      setIsModalOpen(true);
      setModalLoading(true);
      setModalData(null);

      try {
        // Get the order_id from the row data
        const orderId = rowData['order_id'];

        if (orderId) {
          const response = await onOrderClick(orderId);
          setModalData(response);
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        setModalData({ error: 'Failed to load order details' });
      } finally {
        setModalLoading(false);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRowData(null);
  };

  const renderModalTitle = () => {
    if (!selectedRowData || !moreDetailsConfig?.title) return 'More Details';

    if (typeof moreDetailsConfig.title === 'string') {
      return moreDetailsConfig.title;
    } else if (typeof moreDetailsConfig.title === 'object' && moreDetailsConfig.title.accessor) {
      return selectedRowData[moreDetailsConfig.title.accessor] || 'More Details';
    }

    return 'More Details';
  };

  const renderModalContent = () => {
    if (!selectedRowData || !moreDetailsConfig?.content) return null;

    if (typeof moreDetailsConfig.content === 'string') {
      return <div className="description">{moreDetailsConfig.content}</div>;
    } else if (typeof moreDetailsConfig.content === 'object') {
      const { section_columns, description } = moreDetailsConfig.content;

      return (
        <div>
          {section_columns && section_columns.length > 0 && (
            <div className="section-grid">
              {section_columns.map((section, index) => (
                <div key={index} className="section-item">
                  <strong>{section.title}:</strong>
                  <span>{selectedRowData[section.accessor] || '-'}</span>
                </div>
              ))}
            </div>
          )}
          {description && (
            <div className="description">
              {typeof description === 'string'
                ? description
                : selectedRowData[description.accessor] || 'No description available'}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const renderModalTableData = () => {
    if (!selectedRowData) return null;

    if (modalLoading) {
      return (
        <div className="table-data-section">
          <div className="loading-container">
            <LoadingAnimation />
            <div className="loading-text">Loading...</div>
          </div>
        </div>
      );
    }

    if (modalData?.error) {
      return (
        <div className="table-data-section">
          <div className="error-container">
            <div className="error-icon">
              <IoWarning />
            </div>
            <div className="error-content">
              <h3>Unable to Load Order Details</h3>
              <div className="error-message">
                {modalData.error ||
                  'Something went wrong while fetching the order details. Please try again.'}
              </div>
              <button
                className="retry-button"
                onClick={() => {
                  if (selectedRowData) {
                    handlePrimaryCellClick(selectedRowData);
                  }
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!modalData?.table_data) {
      return (
        <div className="table-data-section">
          <div className="no-data">
            <div className="no-data-icon">📋</div>
            <h3>No Details Available</h3>
            <p>There are no additional details to display for this order.</p>
          </div>
        </div>
      );
    }

    const { title, columnDefs, data } = modalData.table_data;

    if (!data || data.length === 0) {
      return (
        <div className="table-data-section">
          <h3>{title}</h3>
          <div className="no-data">No modification history found</div>
        </div>
      );
    }

    return (
      <div className="table-data-section">
        <h3>{title}</h3>
        <div className="modal-table-wrapper">
          <table className="modal-table">
            <thead>
              <tr>
                {columnDefs.map((col: ColumnDef) => (
                  <th key={col.accessor}>{formatColumnHeader(col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row: Record<string, any>, index: number) => (
                <tr key={index}>
                  {columnDefs.map((col: ColumnDef) => (
                    <td key={col.accessor}>
                      {col.type === 'Date' || col.type === 'DateOnly'
                        ? row[col.accessor] && row.offset
                          ? formatDateWithTimezoneOffset(
                              row[col.accessor].toString(),
                              row.offset,
                              col.type === 'Date' ? 'yyyy-MM-dd hh:mm:ss aa' : 'MMM dd, yyyy'
                            )
                          : '-'
                        : col.type === 'Number'
                          ? formatNumber(parseFloat(row[col.accessor] || 0))
                          : row[col.accessor] || '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const { rows, columns, aggregatedData, showTableHeader, primaryColumnId } = useMemo(() => {
    let rows: Array<Record<string, any>> = [];
    let columns: Array<any> = [];
    let aggregatedData: Array<any> = [];
    const showTableHeader = true;
    let primaryColumnId: string | null = null;

    if (config?.columnDefs && data) {
      // Find the primary column
      const primaryColumn = config.columnDefs.find((col) => col.is_primary);
      primaryColumnId = primaryColumn?.accessor || null;

      columns = config.columnDefs.map((columnDef) =>
        columnHelper.accessor(columnDef.accessor, {
          id: columnDef.accessor,
          header: formatColumnHeader(columnDef),
          cell: (info) => info.getValue(),
          meta: columnDef,
          enableSorting:
            columnDef.enable_sorting === true &&
            (columnDef.type === 'Date' ||
              columnDef.type === 'DateOnly' ||
              columnDef.type === 'Number')
        })
      );
      rows = data;
      aggregatedData = getAggregatedData(data, config.columnDefs);
    }
    return {
      rows,
      columns,
      aggregatedData,
      showTableHeader,
      primaryColumnId
    };
  }, [data, config]);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  if (!config?.columnDefs || !data || data.length === 0) {
    return <div>No data available</div>;
  }

  return (
    <>
      <TableWrapper style={{ width, height }}>
        <table className="bordered" style={{ width: width - 15 }}>
          {showTableHeader && (
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <th>
                    <span>#</span>
                  </th>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.getCanSort() ? 'sortable' : ''}
                      onClick={() => header.column.getCanSort() && header.column.toggleSorting()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="sort-icon">
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === 'desc' ? (
                              <BsArrowDownShort />
                            ) : (
                              <BsArrowUpShort />
                            )
                          ) : (
                            ''
                          )}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {table.getSortedRowModel().rows.map((row, rowIndex) => (
              <tr key={row.id}>
                <td>
                  <span>{rowIndex + 1}</span>
                </td>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cell.column.id === primaryColumnId ? 'primary-cell' : ''}
                    onClick={() => {
                      if (cell.column.id === primaryColumnId) {
                        handlePrimaryCellClick(row.original);
                      }
                    }}
                  >
                    {formatCell({ cell })}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {aggregatedData.length > 0 && (
            <tfoot>
              <tr>
                <td></td>
                {aggregatedData.map((data, i) => (
                  <td key={i}>{data}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </TableWrapper>

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()} mode={theme}>
              <ModalHeader>
                <h2>{renderModalTitle()}</h2>
                <CloseButton onClick={closeModal}>
                  <IoClose />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                {renderModalContent()}
                {renderModalTableData()}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>,
          document.body
        )}
    </>
  );
};

export default TableVisual;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div<{ mode: string }>`
  background-color: ${({ theme }) => theme.modal.background};
  border-radius: 16px;
  max-width: 1000px;
  width: 95%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid ${({ theme }) => theme.border_color};
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    width: 95%;
    max-width: 95%;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.border_color};
  background-color: ${({ theme }) => theme.modal.background};
  border-radius: 16px 16px 0 0;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.modal.text};
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.025em;
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;

    h2 {
      font-size: 1.75rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem 1.25rem;

    h2 {
      font-size: 1.5rem;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: ${({ theme }) => theme.modal.text};
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.modal.text};
    background-color: ${({ theme }) => theme.modal.hover};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
  color: ${({ theme }) => theme.modal.text};

  .section-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .section-item {
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background-color: ${({ theme }) => theme.background};
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border_color};

    strong {
      color: ${({ theme }) => theme.modal.text};
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      opacity: 0.8;
      margin-bottom: 0.5rem;
    }

    span {
      font-size: 1rem;
      font-weight: 500;
      color: ${({ theme }) => theme.modal.text};
      word-break: break-word;
    }
  }

  .description {
    margin-top: 1.5rem;
    padding: 1.5rem;
    background-color: ${({ theme }) => theme.background};
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border_color};
    line-height: 1.6;
    font-size: 1.125rem;
    color: ${({ theme }) => theme.modal.text};
    opacity: 0.9;
  }

  .table-data-section {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid ${({ theme }) => theme.border_color};

    h3 {
      font-size: 1.25rem;
      font-weight: 700;
      color: ${({ theme }) => theme.modal.text};
      margin-bottom: 1rem;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 2rem;
      min-height: 150px;
      .loading-text {
        font-size: 14px;
        color: #91a1ac;
        font-weight: 500;
      }
    }

    .error-container {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      padding: 1.5rem;
      background-color: ${(props) => (props.mode === 'dark' ? '#2d1b1b' : '#fef2f2')};
      border: 1px solid ${(props) => (props.mode === 'dark' ? '#4a2d2d' : '#fecaca')};
      border-radius: 12px;
      margin: 1rem 0;

      .error-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background-color: ${(props) => (props.mode === 'dark' ? '#3d2626' : '#fee2e2')};
        border-radius: 50%;
        color: ${(props) => (props.mode === 'dark' ? '#ef4444' : '#dc2626')};
        font-size: 20px;
      }

      .error-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: ${(props) => (props.mode === 'dark' ? '#ef4444' : '#991b1b')};
          margin: 0;
        }

        .error-message {
          font-size: 0.875rem;
          color: ${(props) => (props.mode === 'dark' ? '#fca5a5' : '#7f1d1d')};
          line-height: 1.5;
          margin: 0;
        }

        .retry-button {
          align-self: flex-start;
          padding: 0.5rem 1rem;
          background-color: ${(props) => (props.mode === 'dark' ? '#ef4444' : '#dc2626')};
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;

          &:hover {
            background-color: ${(props) => (props.mode === 'dark' ? '#dc2626' : '#b91c1c')};
            transform: translateY(-1px);
          }

          &:active {
            transform: translateY(0);
          }
        }
      }
    }

    .no-data {
      text-align: center;
      padding: 2rem;
      background-color: ${(props) => (props.mode === 'dark' ? '#1f2937' : '#f8fafc')};
      border: 1px solid ${(props) => (props.mode === 'dark' ? '#374151' : '#e2e8f0')};
      border-radius: 12px;
      margin: 1rem 0;

      .no-data-icon {
        font-size: 2.5rem;
        margin-bottom: 1rem;
        opacity: 0.6;
      }

      h3 {
        font-size: 1.125rem;
        font-weight: 600;
        color: ${(props) => (props.mode === 'dark' ? '#f3f4f6' : '#374151')};
        margin: 0 0 0.5rem 0;
      }

      p {
        font-size: 0.875rem;
        color: ${(props) => (props.mode === 'dark' ? '#9ca3af' : '#6b7280')};
        margin: 0;
        line-height: 1.5;
      }
    }

    .modal-table-wrapper {
      overflow-x: auto;
      overflow-y: auto;
      max-height: 400px; /* Fixed height for the table container */
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: -ms-autohiding-scrollbar;
      border: 1px solid ${({ theme }) => theme.border_color};
      border-radius: 8px;
    }

    .modal-table {
      border-spacing: 0;
      border-collapse: collapse;
      width: 100%;
      min-width: 600px; /* Ensure a minimum width for the table */
      table-layout: auto; /* Use auto layout for flexible column widths */

      th {
        font-size: 14px;
        color: ${({ theme }) => theme.modal.text};
        font-weight: 600;
        text-align: left;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.border_color};
        background-color: ${({ theme }) => theme.modal.background};
        word-wrap: break-word;
        vertical-align: top;
        max-width: 300px; /* General max-width for all header cells */
        position: sticky;
        top: 0;
        z-index: 10;
      }

      td {
        font-size: 14px;
        color: ${({ theme }) => theme.modal.text};
        font-weight: 400;
        text-align: left;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.border_color};
        word-wrap: break-word;
        word-break: break-word;
        vertical-align: top;
        line-height: 1.5;
        max-width: 300px; /* General max-width for all data cells */
      }

      tr:last-child td {
        border-bottom: none;
      }
    }
  }

  @media (max-width: 1024px) {
    .section-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;

    .section-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .description {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;

    .section-grid {
      grid-template-columns: 1fr;
    }

    .section-item {
      padding: 0.875rem;

      strong {
        font-size: 0.8rem;
      }

      span {
        font-size: 0.875rem;
      }
    }

    .description {
      font-size: 0.9rem;
      padding: 1.25rem;
    }

    .table-data-section {
      .modal-table-wrapper {
        max-height: 300px; /* Smaller height for mobile */
      }

      .modal-table {
        min-width: 400px;

        th,
        td {
          padding: 0.5rem 0.75rem;
          font-size: 12px;
          max-width: 200px; /* Smaller max-width for mobile */
        }
      }
    }
  }
`;

const TableWrapper = styled.div`
  background-color: ${({ theme }) => theme.widget.background};
  padding-left: 10px;
  overflow: auto;
  height: 100%;

  table {
    border-spacing: 0;
    background-color: ${({ theme }) => theme.widget.background};
    border-collapse: separate;
    width: 100%;

    > thead {
      height: 55px;
      z-index: 100;
      position: sticky;
      top: 0;

      > tr {
        > th {
          font-size: 16px;
          color: ${({ theme }) => theme.text};
          font-weight: 700;
          position: relative;
          white-space: nowrap;
          padding: 0 0.5rem;
          border-bottom: 2px solid ${({ theme }) => theme.border_color};
          background-color: ${({ theme }) => theme.widget.background};

          &:first-child {
            > span {
              opacity: 0.4;
            }
          }

          &.sortable {
            cursor: pointer;
            user-select: none;

            &:hover {
              background-color: ${({ theme }) => theme.widget.background};
              opacity: 0.8;
            }
          }

          .sort-icon {
            margin-left: 4px;
            display: inline-flex;
            align-items: center;
          }
        }
      }
    }

    > tbody {
      > tr {
        > td {
          font-size: 15px;
          font-weight: 400;
          line-height: 21.48px;
          padding: 0 0.5rem;
          white-space: nowrap;
          border-bottom: 1px solid ${({ theme }) => theme.border_color};
          color: ${({ theme }) => theme.text};

          &:first-child {
            > span {
              opacity: 0.4;
            }
          }

          &.primary-cell {
            cursor: pointer;
            color: ${({ theme }) => theme.primary_color || '#007bff'};
            text-decoration: underline;

            &:hover {
              opacity: 0.8;
            }
          }
        }
        height: 49px;

        &:hover {
          background-color: ${({ theme }) => theme.widget.background};
          opacity: 0.9;
        }
      }
    }

    > tfoot {
      bottom: 0px;
      height: 55px;
      position: sticky;

      > tr {
        > td {
          font-size: 16px;
          color: ${({ theme }) => theme.text};
          font-weight: 700;
          position: relative;
          white-space: nowrap;
          padding: 0 0.5rem;
          border-top: 2px solid ${({ theme }) => theme.border_color};
          background-color: ${({ theme }) => theme.widget.background};
        }
      }
    }
  }
`;
